import { useState, useEffect } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import { useAuth } from '../../../hooks/useAuth';
import { dbHelpers } from '../../../lib/supabase';

interface ChecklistItem {
  id: string;
  title: string;
  category: string;
  completed: boolean;
  created_at: string;
}

const categories = [
  'Visa & Immigration',
  'Housing',
  'Finance & Banking',
  'Healthcare',
  'Moving & Logistics',
  'Pet Relocation',
  'Education',
  'Other'
];

const defaultChecklistItems = [
  { title: 'Research visa requirements', category: 'Visa & Immigration', completed: false },
  { title: 'Apply for appropriate visa', category: 'Visa & Immigration', completed: false },
  { title: 'Gather required documents', category: 'Visa & Immigration', completed: false },
  { title: 'Research neighborhoods in Ireland', category: 'Housing', completed: false },
  { title: 'Contact real estate agents', category: 'Housing', completed: false },
  { title: 'Schedule property viewings', category: 'Housing', completed: false },
  { title: 'Open Irish bank account', category: 'Finance & Banking', completed: false },
  { title: 'Transfer funds internationally', category: 'Finance & Banking', completed: false },
  { title: 'Set up direct debits', category: 'Finance & Banking', completed: false },
  { title: 'Register with GP', category: 'Healthcare', completed: false },
  { title: 'Get European Health Insurance Card', category: 'Healthcare', completed: false },
  { title: 'Transfer medical records', category: 'Healthcare', completed: false },
  { title: 'Book international movers', category: 'Moving & Logistics', completed: false },
  { title: 'Sort and pack belongings', category: 'Moving & Logistics', completed: false },
  { title: 'Book flights', category: 'Moving & Logistics', completed: false },
  { title: 'Check pet import requirements', category: 'Pet Relocation', completed: false },
  { title: 'Get pet microchipped', category: 'Pet Relocation', completed: false },
  { title: 'Book pet transport', category: 'Pet Relocation', completed: false },
  { title: 'Research schools', category: 'Education', completed: false },
  { title: 'Apply for school places', category: 'Education', completed: false },
  { title: 'Cancel US subscriptions', category: 'Other', completed: false },
  { title: 'Forward mail', category: 'Other', completed: false },
  { title: 'Say goodbye to friends', category: 'Other', completed: false }
];

export default function Checklist() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [newItemCategory, setNewItemCategory] = useState(categories[0]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    loadChecklist();
  }, [user, authLoading]);

  const loadChecklist = async () => {
    if (authLoading) return;

    try {
      if (user) {
        // Load from database for logged-in users
        const data = await dbHelpers.getChecklistItems(user.id);
        
        // If user has no items, initialize with defaults
        if (data.length === 0) {
          const initialItems = defaultChecklistItems.map((item, index) => ({
            id: `temp-${index}`,
            ...item,
            created_at: new Date().toISOString()
          }));
          setItems(initialItems);
        } else {
          setItems(data);
        }
      } else {
        // Load from localStorage for guest users
        const savedItems = localStorage.getItem('guestChecklistItems');
        if (savedItems) {
          setItems(JSON.parse(savedItems));
        } else {
          // Initialize with default items
          const initialItems = defaultChecklistItems.map((item, index) => ({
            id: `guest-${index}`,
            ...item,
            created_at: new Date().toISOString()
          }));
          setItems(initialItems);
          localStorage.setItem('guestChecklistItems', JSON.stringify(initialItems));
        }
      }
    } catch (error) {
      console.error('Error loading checklist:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveToLocalStorage = (updatedItems: ChecklistItem[]) => {
    if (!user) {
      localStorage.setItem('guestChecklistItems', JSON.stringify(updatedItems));
    }
  };

  const handleToggleComplete = async (id: string) => {
    const item = items.find(i => i.id === id);
    if (!item) return;

    const updatedItems = items.map(i =>
      i.id === id ? { ...i, completed: !i.completed } : i
    );
    setItems(updatedItems);

    if (user) {
      try {
        await dbHelpers.updateChecklistItem(id, !item.completed);
      } catch (error) {
        console.error('Error updating item:', error);
        setItems(items);
      }
    } else {
      saveToLocalStorage(updatedItems);
    }
  };

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemTitle.trim()) return;

    const newItem: ChecklistItem = {
      id: user ? `temp-${Date.now()}` : `guest-${Date.now()}`,
      title: newItemTitle,
      category: newItemCategory,
      completed: false,
      created_at: new Date().toISOString()
    };

    const updatedItems = [...items, newItem];
    setItems(updatedItems);

    if (user) {
      try {
        const createdItem = await dbHelpers.createChecklistItem(user.id, newItemTitle, newItemCategory);
        setItems(items.map(i => i.id === newItem.id ? createdItem : i).concat(createdItem.id === newItem.id ? [] : [createdItem]));
      } catch (error) {
        console.error('Error creating item:', error);
        setItems(items);
      }
    } else {
      saveToLocalStorage(updatedItems);
    }

    setNewItemTitle('');
    setShowAddForm(false);
  };

  const handleDeleteItem = (id: string) => {
    const updatedItems = items.filter(i => i.id !== id);
    setItems(updatedItems);
    
    if (!user) {
      saveToLocalStorage(updatedItems);
    }
  };

  const filteredItems = selectedCategory
    ? items.filter(item => item.category === selectedCategory)
    : items;

  const completedCount = items.filter(item => item.completed).length;
  const totalCount = items.length;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const getCategoryCount = (category: string) => {
    return items.filter(item => item.category === category).length;
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar scrolled={true} />

      <div className="pt-24 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-[#0A381F] mb-4">Moving to Ireland Checklist</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Stay organized with your relocation journey. Track your progress and never miss an important task.
            </p>
            {!user && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg max-w-2xl mx-auto">
                <p className="text-sm text-amber-800">
                  <i className="ri-information-line mr-2"></i>
                  You're using the checklist as a guest. Your progress is saved locally. 
                  <a href="/auth/signup" className="font-semibold underline ml-1 cursor-pointer hover:text-amber-900">Sign up</a> to sync across devices.
                </p>
              </div>
            )}
          </div>

          {/* Progress Overview */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-[#0A381F] mb-2">Your Progress</h2>
                <p className="text-gray-600">
                  {completedCount} of {totalCount} tasks completed
                </p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold text-[#D4AF37]">{progressPercentage}%</div>
                <div className="text-sm text-gray-500">Complete</div>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className="bg-gradient-to-r from-[#D4AF37] to-[#C19B2F] h-full rounded-full transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar - Categories */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 lg:sticky lg:top-24">
                <h3 className="text-lg font-bold text-[#0A381F] mb-4">Categories</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      selectedCategory === null
                        ? 'bg-[#D4AF37] text-white'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">All Tasks</span>
                      <span className="text-sm">{totalCount}</span>
                    </div>
                  </button>
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        selectedCategory === category
                          ? 'bg-[#D4AF37] text-white'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{category}</span>
                        <span className="text-xs">{getCategoryCount(category)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content - Checklist Items */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-[#0A381F]">
                    {selectedCategory || 'All Tasks'}
                  </h2>
                  <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="px-6 py-3 bg-[#D4AF37] text-white rounded-lg font-semibold hover:bg-[#C19B2F] transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-2"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`ri-${showAddForm ? 'close' : 'add'}-line text-lg`}></i>
                    </div>
                    {showAddForm ? 'Cancel' : 'Add Task'}
                  </button>
                </div>

                {/* Add Item Form */}
                {showAddForm && (
                  <form onSubmit={handleAddItem} className="mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-[#0A381F] mb-2">
                          Task Title
                        </label>
                        <input
                          type="text"
                          value={newItemTitle}
                          onChange={(e) => setNewItemTitle(e.target.value)}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                          placeholder="Enter task description..."
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#0A381F] mb-2">
                          Category
                        </label>
                        <select
                          value={newItemCategory}
                          onChange={(e) => setNewItemCategory(e.target.value)}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm cursor-pointer"
                        >
                          {categories.map(category => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="submit"
                        className="w-full py-3 bg-[#D4AF37] text-white rounded-lg font-semibold hover:bg-[#C19B2F] transition-all duration-300 cursor-pointer whitespace-nowrap"
                      >
                        Add Task
                      </button>
                    </div>
                  </form>
                )}

                {/* Checklist Items */}
                <div className="space-y-3">
                  {filteredItems.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
                        <i className="ri-checkbox-circle-line text-3xl text-gray-400"></i>
                      </div>
                      <p className="text-gray-500">No tasks in this category yet.</p>
                      <button
                        onClick={() => setShowAddForm(true)}
                        className="mt-4 text-[#D4AF37] hover:text-[#C19B2F] font-semibold cursor-pointer"
                      >
                        Add your first task
                      </button>
                    </div>
                  ) : (
                    filteredItems.map(item => (
                      <div
                        key={item.id}
                        className={`flex items-center gap-4 p-4 rounded-lg border transition-all duration-200 ${
                          item.completed
                            ? 'bg-green-50 border-green-200'
                            : 'bg-white border-gray-200 hover:border-[#D4AF37]'
                        }`}
                      >
                        <button
                          onClick={() => handleToggleComplete(item.id)}
                          className="flex-shrink-0 w-6 h-6 flex items-center justify-center cursor-pointer"
                        >
                          {item.completed ? (
                            <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                          ) : (
                            <i className="ri-checkbox-blank-circle-line text-2xl text-gray-400 hover:text-[#D4AF37]"></i>
                          )}
                        </button>
                        <div className="flex-1">
                          <p className={`font-medium ${item.completed ? 'text-gray-500 line-through' : 'text-[#0A381F]'}`}>
                            {item.title}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">{item.category}</p>
                        </div>
                        <button
                          onClick={() => handleDeleteItem(item.id)}
                          className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-lg"></i>
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
